/**
 * 이상한 방 - 코어 상태 타입
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md`
 *
 * 이 파일의 모든 타입은 두 원칙에서 역산됐다.
 *  - 원칙 1(1절): 상태 필드는 체인저의 변화 동사에서 역산한다.
 *    필드가 동사보다 많으면 "만들 수 없는데 보이는 차이"가, 적으면 "만들 수 있는데 판정 못 하는 변화"가 생긴다.
 *  - 원칙 2(1절): 물리적으로 이상한 건 허용, 시각적으로 찾을 수 없는 건 금지.
 *
 * 모든 타입이 `Readonly`인 이유: `original`(외우기 종료 스냅샷)과 `mutated`(체인저 작업 결과)
 * 두 상태를 동시에 보관하는데(2절), 어느 한쪽을 제자리 변경하면 diff의 진실성이 무너진다.
 * 상태 변경은 항상 새 객체를 반환하는 방식으로만 한다.
 */

/** 배치 시 발급되는 오브젝트 식별자. 2절: 재사용 절대 금지 - 재사용하면 제거/추가 diff가 뒤섞인다. */
export type InstanceId = string

/** 카탈로그(소품 도감) 항목 식별자. 어떤 소품인가만 가리키며 인스턴스 상태는 담지 않는다. */
export type CatalogId = string

export type PlayerId = string

/**
 * 팔레트 인덱스. 4절: 색은 연속값(RGB)이 아니라 8~12색 팔레트의 인덱스다.
 * 연속값을 허용하면 "미묘하게 다른 회색" 같은 찾을 수 없는 변화가 가능해지고,
 * 이는 난이도가 아니라 사기다(탄환 경제 하에서 억울한 탈락으로 직결).
 */
export type PaletteIndex = number

/**
 * 회전 - 90° 4단계 이산값. 4절과 동일한 이유로 연속 각도를 금지한다.
 * 부수 효과: diff가 정수 비교로 끝나 부동소수점 오차 판정 이슈가 존재하지 않는다.
 */
export type Rot = 0 | 1 | 2 | 3

/**
 * 3D 정수 그리드 좌표. 3절: 바닥·벽·가구 표면을 전부 이 단일 체계로 표현하고
 * 부모-자식 관계를 도입하지 않는다.
 * "책상 위 머그컵"은 책상의 자식이 아니라 책상보다 y가 높은 셀에 있는 머그컵일 뿐이다.
 * 공중부양을 허용하기로 한 결정(원칙 2)이 이 단순화를 가능하게 했다.
 */
export type Cell3 = Readonly<{ x: number; y: number; z: number }>

/**
 * 방에 배치된 오브젝트 하나. 필드 목록이 곧 변화 동사 목록이다(5.2절).
 *  cell → 이동 / rot → 회전 / colorId → 색 변경 / 존재 여부 → 추가·제거
 */
export type ObjectInstance = Readonly<{
  id: InstanceId
  catalogId: CatalogId
  cell: Cell3
  rot: Rot
  colorId: PaletteIndex
  /**
   * 배치자. 2.1절: 체인저의 심리전 재료이며 **서버 전용 필드**다.
   * 어떤 페이즈에서도 클라이언트로 직렬화되지 않는다.
   */
  placedBy: PlayerId
}>

/**
 * 방의 전체 상태. 2절: 서버는 라운드당 이 스냅샷을 두 벌 보관한다(original / mutated).
 * 진실의 원천은 스냅샷이며, 체인저의 조작 로그는 리빌 연출용 보조 데이터일 뿐 판정 근거가 아니다.
 */
export type RoomState = Readonly<{
  templateId: string
  objects: ReadonlyMap<InstanceId, ObjectInstance>
}>

/**
 * 방의 그리드 크기(셀 단위, 미터 아님).
 * 3.1절: 그리드가 유한하다는 사실 자체가 "방 밖 배출" 익스플로잇을 자동 차단한다.
 */
export type RoomBounds = Readonly<{ sizeX: number; sizeY: number; sizeZ: number }>

/**
 * 소품의 회전 대칭 차수 - 서로 구별되는 회전 상태의 개수.
 *  1 = 4방향 모두 구별됨 / 2 = 180° 대칭(2방향만 구별) / 4 = 완전 대칭
 *
 * 4절: 원형 러그·정사각 협탁은 90° 돌려도 티가 나지 않는다.
 * 그런 소품에 회전을 허용하면 "찾을 수 없는 변화"가 되므로 4일 때 회전 동사를 비활성화한다.
 * 난이도는 값이 아니라 카탈로그에서 나온다.
 */
export type RotSymmetry = 1 | 2 | 4

/** 카탈로그 한 항목의 규격. 8절의 카탈로그 규격 요건을 타입으로 굳힌 것. */
export type CatalogEntry = Readonly<{
  id: CatalogId
  name: string
  /** 점유 셀 크기(각 축의 셀 개수, 1 이상). 8절: 바운딩 박스는 그리드 배수로 정리돼야 한다. */
  footprint: Cell3
  rotSymmetry: RotSymmetry
  defaultColorId: PaletteIndex
}>

/**
 * 체인저의 변화 동사(5.2절).
 * 컨셉시트의 '맞교환'은 제외됐다 - 5.1절("한 오브젝트 = 최대 한 변화") 하에서
 * 맞교환은 자동으로 예산 2·판정 2가 되므로 이동 두 번의 UI 단축키일 뿐 독립 동사가 아니다.
 */
export type Verb = 'add' | 'remove' | 'move' | 'rotate' | 'recolor'

/**
 * 다섯 동사의 **정본 순서**. 생성기가 배분표를 세는 순서(`changeGen`) · 호스트가 동사별 개수를 세는 순서(`matchView.verbCountsFor`) ·
 * 체인저 팔레트의 버튼 순서(`input/changerToolTypes`) · 튜닝의 허용 동사 기본값이 전부 이 배열 하나를 가리킨다.
 * 한때 세 파일이 같은 리터럴을 각자 들고 있었다 - 순서가 갈리면 화면이 읊는 개수 순서와 호스트가 센 순서가 어긋나고,
 * 생성기 결정론(같은 시드 → 같은 변화)까지 흔들린다. 단계 안내 화면(`ui/levelIntro`)만 사람이 읽는 순서를 따로 든다.
 */
export const VERBS: readonly Verb[] = ['add', 'remove', 'move', 'rotate', 'recolor']

/** `Rot`의 단계 수. 90° × 4(4절: 연속 각도 금지). `Rot`이 0~3인 것과 같은 값이라 타입 옆에 둔다. */
export const ROT_STEP_COUNT = 4
/** 반회전(180°)의 단계 수. `oppositeRot`(`furnitureGroups`)과 회전 대칭 판정(`changeGen.rotSymmetry`)이 같은 값을 각자 들고 있었다(P1 정리). */
export const ROT_HALF_TURN = ROT_STEP_COUNT / 2

/** 라운드 룰 파라미터. 7절(컨셉시트): 전부 플레이테스트로만 답이 나오는 수치라 설정으로 분리한다. */
export type RoomConfig = Readonly<{
  allowedVerbs: ReadonlySet<Verb>
  /** 체인저가 만들어야 하는 변화 개수. 5.1절에 의해 "건드린 오브젝트 수"와 1:1이다. */
  changeCount: number
  /** 꾸미기 페이즈의 인당 배치 수 제한. */
  placementsPerPlayer: number
}>

/**
 * 그리드 셀 한 변의 실제 크기(미터).
 * 카탈로그 최소 소품의 바운딩 크기에서 온 값이며, 1주차에는 0.25m로 고정한다.
 * 렌더 좌표 변환은 전부 이 상수를 거쳐야 한다 - 축척을 손으로 곱하는 순간 그리드가 어긋난다.
 */
export const CELL_SIZE_METERS = 0.25
